/**
 * Authentication Hooks
 * Coláiste Lurgan Management System - Auth state management with httpOnly cookies
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState } from 'react';
import { api, initializeCSRFToken } from '../lib/api';
import { queryKeys, invalidateQueries } from '../lib/queryClient';

// Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export function AuthProvider({ children }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const queryClient = useQueryClient();

  // Check if user has auth token
  const hasAuthToken = !!localStorage.getItem('authToken');

  // Get user profile (only if we have a token)
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: queryKeys.auth.profile(),
    queryFn: api.auth.getProfile,
    enabled: hasAuthToken, // Only run if we have a token
    retry: (failureCount, error) => {
      // Don't retry if user is not authenticated
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: api.auth.login,
    onSuccess: (data) => {
      // Store auth token in localStorage
      if (data.token) {
        localStorage.setItem('authToken', data.token);
      }

      // Clear admin-only storage keys for non-admin roles (e.g. a user previously had a staff
      // role but is now logging in as PARENT — stale staff data should not persist)
      if (data.user?.user_type === 'PARENT') {
        ['bulk_upload_field_mapping', 'export_field_selection'].forEach(k =>
          localStorage.removeItem(k)
        );
        sessionStorage.removeItem('activeEmailTask');
      }

      // Set user data in cache with correct format (profile API expects {user: ...})
      queryClient.setQueryData(queryKeys.auth.profile(), { user: data.user });

      // Invalidate and refetch all auth-related queries
      invalidateQueries.auth();
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: api.auth.logout,
    onSuccess: () => {
      // Clear auth token from localStorage
      localStorage.removeItem('authToken');
      // Clear all user data from cache
      queryClient.clear();
      // Navigate to login page using window.location (works outside Router context)
      window.location.href = '/login';
    },
    onError: (error) => {
      console.error('Logout error:', error);
      // Even if logout fails on server, clear local data
      localStorage.removeItem('authToken');
      queryClient.clear();
      // Navigate to login page even on error
      window.location.href = '/login';
    },
  });

  // Listen for auth errors (401 responses)
  useEffect(() => {
    const handleAuthError = () => {
      localStorage.removeItem('authToken');
      queryClient.clear();
    };

    window.addEventListener('auth-error', handleAuthError);
    return () => window.removeEventListener('auth-error', handleAuthError);
  }, [queryClient]);

  // Initialize CSRF token on app startup
  useEffect(() => {
    const initAuth = async () => {
      try {
        await initializeCSRFToken();
      } catch (error) {
        console.warn('Failed to initialize CSRF token on startup:', error);
      }
    };
    
    initAuth();
  }, []);

  // Mark as initialized once we've attempted to load user
  useEffect(() => {
    // If no token, immediately mark as initialized (no need to wait for API call)
    if (!hasAuthToken) {
      setIsInitialized(true);
      return;
    }

    // If we have a token, wait for the user query to finish
    if (!isLoadingUser) {
      setIsInitialized(true);
    }
  }, [isLoadingUser, hasAuthToken]);

  const value = {
    // User state
    user: user?.user || null,
    isAuthenticated: !!user?.user && !userError,
    isLoading: isLoadingUser,
    isInitialized,
    
    // Actions
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    refetchUser,
    
    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Auth hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// User permissions hook
export function useUserPermissions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Get permissions from the existing user profile data instead of making a separate call
  // This reuses the same data that useAuth already fetched
  const profileData = queryClient.getQueryData(queryKeys.auth.profile());
  const permissions = profileData?.user?.permissions || [];

  const hasPermission = (permission) => {
    // ADMIN users have all permissions
    if (user?.user_type === 'ADMIN') return true;
    
    if (!permissions) return false;
    return permissions.some(p => p.codename === permission);
  };

  const hasAnyPermission = (permissionList) => {
    // ADMIN users have all permissions
    if (user?.user_type === 'ADMIN') return true;
    
    if (!permissions) return false;
    return permissionList.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissionList) => {
    // ADMIN users have all permissions
    if (user?.user_type === 'ADMIN') return true;
    
    if (!permissions) return false;
    return permissionList.every(permission => hasPermission(permission));
  };

  return {
    permissions,
    isLoading: false, // No longer loading separately since we use cached data
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userType: user?.user_type,
    isAdmin: user?.user_type === 'ADMIN',
    isTeacher: user?.user_type === 'TEACHER',
    isOffice: user?.user_type === 'OFFICE',
  };
}

// Hook for authentication guards
export function useRequireAuth() {
  const { isAuthenticated, isLoading, isInitialized } = useAuth();
  
  return {
    isAuthenticated,
    isLoading: isLoading || !isInitialized,
    shouldRedirect: isInitialized && !isLoading && !isAuthenticated,
  };
}

// Registration hook (if needed)
export function useRegister() {
  return useMutation({
    mutationFn: api.auth.register,
    onSuccess: () => {
      // Registration successful, but user still needs to verify email
      // Don't automatically log them in
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });
}

export default useAuth;